import { useCallback, useEffect, useRef, useState } from "react";

import agent from "../utils/axios";
import { Authenticate } from "../types/Authenticate";

interface AuthUrlResponse {
  url: string;
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const POLL_MS = 200;

/**
 * The QuickBooks OAuth sign-in popup, shared by Connect QuickBooks, Pending bills and
 * Bill details (the same flow those screens each carry today):
 * GET the auth URL, open a 600x700 popup, poll it until QuickBooks redirects back with
 * `code=`, then POST QuickBooks/Authenticate?redirectUri&realmId with `{ code }` so CSI
 * Pay stores its own token for that company (RealmId).
 *
 *   const { authenticate, inProgress } = useQuickBooksAuthPopup();
 *   const ok = await authenticate(company.realmId); // false if blocked, closed or failed
 *
 * Screens that word each failure differently use `authenticateWithReason`, which says why.
 */
export type QuickBooksAuthFailure =
  | "start-failed" // the auth URL could not be fetched
  | "blocked" // the browser blocked the popup
  | "cancelled" // the popup was closed before QuickBooks redirected back
  | "auth-failed"; // CSI Pay could not exchange the code for a token

export type QuickBooksAuthResult =
  | { ok: true }
  | { ok: false; reason: QuickBooksAuthFailure };

export function useQuickBooksAuthPopup() {
  const [inProgress, setInProgress] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const authenticateWithReason = useCallback(
    async (realmId: string): Promise<QuickBooksAuthResult> => {
      setInProgress(true);
      try {
        let url = "";
        try {
          ({ url } = (await agent.quickbooks.getAuthUrl()) as AuthUrlResponse);
        } catch (e) {
          return { ok: false, reason: "start-failed" };
        }
        if (!url) return { ok: false, reason: "start-failed" };

        const urlParams = new URLSearchParams(new URL(url).search);
        const redirect_uri = urlParams.get("redirect_uri") ?? "";
        const left = window.screen.width / 2 - POPUP_WIDTH / 2;
        const top = window.screen.height / 2 - POPUP_HEIGHT / 2;

        const authWindow = window.open(
          url,
          "QuickBooks Login",
          `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`
        );
        if (!authWindow) return { ok: false, reason: "blocked" };

        // Wait for the popup to come back to our origin with ?code=, or to be closed.
        const code = await new Promise<string | null>((resolve) => {
          const authCheckInterval = setInterval(() => {
            try {
              if (authWindow.location.href.includes("code=")) {
                const params = new URLSearchParams(authWindow.location.search);
                const found = params.get("code");
                if (found) {
                  clearInterval(authCheckInterval);
                  authWindow.close();
                  resolve(found);
                  return;
                }
              }
            } catch (err) {
              // Ignore cross-origin errors until the popup redirects back to our domain.
            }
            if (authWindow.closed) {
              clearInterval(authCheckInterval);
              resolve(null);
            }
          }, POLL_MS);
        });
        if (!code) return { ok: false, reason: "cancelled" };

        // Exchange the code for a token stored under the company the person chose
        // (not the callback's realm).
        const authRequest: Authenticate = { code };
        try {
          await agent.quickbooks.authenticateQuickbooks(
            authRequest,
            redirect_uri,
            realmId
          );
        } catch (e) {
          return { ok: false, reason: "auth-failed" };
        }
        return { ok: true };
      } catch (e) {
        return { ok: false, reason: "auth-failed" };
      } finally {
        if (mounted.current) setInProgress(false);
      }
    },
    []
  );

  const authenticate = useCallback(
    async (realmId: string): Promise<boolean> =>
      (await authenticateWithReason(realmId)).ok,
    [authenticateWithReason]
  );

  return { authenticate, authenticateWithReason, inProgress };
}

export default useQuickBooksAuthPopup;
